import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { localToday, localDaysAgo } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, History, CheckCircle, XCircle, Clock } from 'lucide-react';

const ACTION_CONFIG = {
  approve: { 
    icon: CheckCircle, 
    color: 'text-green-500',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-800'
  },
  reject: { 
    icon: XCircle, 
    color: 'text-red-500',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800'
  },
  modify: { 
    icon: Clock, 
    color: 'text-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800'
  },
  resubmit: { 
    icon: Clock, 
    color: 'text-amber-500',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-200 dark:border-amber-800'
  }
};

function AuditRow({ entry, t }) {
  const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.modify;
  const Icon = config.icon;
  const timestamp = new Date(entry.timestamp);
  
  return (
    <div className={`p-3 rounded-lg border ${config.bgColor} ${config.borderColor}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 ${config.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-sm">{entry.worker_name}</span>
            <Badge variant="outline" className="text-xs shrink-0">
              {t(`time.action.${entry.action}`)}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {t('time.by')}: {entry.performer_name}
          </p>
          {entry.notes && (
            <p className="text-sm mt-2 text-muted-foreground italic">
              "{entry.notes}"
            </p>
          )}
          {entry.details?.date && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('common.date')}: {entry.details.date} • {entry.details.hours || 0}h
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            {timestamp.toLocaleDateString('ru-RU')} {timestamp.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    </div>
  );
}

export function TimeAuditHistoryDialog({ open, onOpenChange, workers = [] }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  
  // Filters
  const [workerId, setWorkerId] = useState('all');
  const [dateFrom, setDateFrom] = useState(() => localDaysAgo(30));
  const [dateTo, setDateTo] = useState(localToday());

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/time/audit-history?date_from=${dateFrom}&date_to=${dateTo}&limit=100`;
      if (workerId !== 'all') {
        url += `&worker_id=${workerId}`;
      }
      const res = await api.get(url);
      setHistory(res.data || []);
    } catch (err) {
      console.error('Failed to fetch audit history:', err);
    } finally {
      setLoading(false);
    }
  }, [api, workerId, dateFrom, dateTo]);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, fetchHistory]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            {t('time.auditHistory')}
          </DialogTitle>
          <DialogDescription>
            {t('time.auditHistoryDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Filters */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('workers.title')}</Label>
              <Select value={workerId} onValueChange={setWorkerId}>
                <SelectTrigger data-testid="audit-worker-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {workers.map(w => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.first_name} {w.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('common.from')}</Label>
              <Input 
                type="date" 
                value={dateFrom} 
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="audit-date-from"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('common.to')}</Label>
              <Input 
                type="date" 
                value={dateTo} 
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="audit-date-to"
              />
            </div>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchHistory} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.filter')}
          </Button>
        </div>

        {/* History List */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{t('time.noAuditHistory')}</p>
            </div>
          ) : (
            <div className="space-y-3 pb-4">
              {history.map(entry => (
                <AuditRow key={entry.id} entry={entry} t={t} />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
